import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import {
  DCF_DECISION_CHECKBOX_FIELDS,
  DCF_DECISION_CHECKBOX_RECTS,
  getDcfSignaturePlacement,
  getDcfSignatureRoleConfig,
} from './dcfSignerRole'

const MARK_WIDTH = 150
const MARK_MIN_HEIGHT = 14
const MARK_MAX_HEIGHT = 36
const NAME_SIZE = 9
const TITLE_SIZE = 8
const META_SIZE = 7
const LINE_GAP = 2
const EDGE_PADDING = 4

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

const toPngBytes = pngDataUrl => {
  let base64 = pngDataUrl || ''
  if (base64.includes(',')) base64 = base64.split(',')[1] || ''
  if (!base64) return null
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/**
 * Tick the Approved / Rejected box for roles that carry a decision.
 *
 * database-service only ticks these when it regenerates the PDF from the
 * template, which it skips for client-stamped uploads, so the box has to be set
 * here or the decision never appears on the document.
 */
const applyDecisionCheckbox = (pdfDoc, role, decision) => {
  if (!getDcfSignatureRoleConfig(role)?.requiresDecision) return
  if (!DCF_DECISION_CHECKBOX_FIELDS[decision]) return

  let form
  try {
    form = pdfDoc.getForm()
  } catch {
    return // Template has no AcroForm; the signature stamp still applies.
  }

  Object.entries(DCF_DECISION_CHECKBOX_FIELDS).forEach(([value, fieldName]) => {
    try {
      const checkBox = form.getCheckBox(fieldName)
      if (value === decision) checkBox.check()
      else checkBox.uncheck()
    } catch {
      // Field missing or not a checkbox on this template revision.
    }
  })

  try {
    form.updateFieldAppearances()
  } catch {
    // Best effort: /V is set, so viewers honouring /NeedAppearances still render it.
  }
}

/** Ink a check into the template box so the decision is visible without AcroForm. */
const drawDecisionMark = (pdfPage, decision) => {
  const box = DCF_DECISION_CHECKBOX_RECTS[decision]
  if (!box || !pdfPage) return
  const pad = 3
  const x = box.x + pad
  const y = box.y + pad
  const w = box.width - pad * 2
  const h = box.height - pad * 2
  const color = rgb(0.05, 0.05, 0.08)
  pdfPage.drawLine({
    start: { x, y: y + h * 0.45 },
    end: { x: x + w * 0.35, y },
    thickness: 1.8,
    color,
  })
  pdfPage.drawLine({
    start: { x: x + w * 0.35, y },
    end: { x: x + w, y: y + h },
    thickness: 1.8,
    color,
  })
}

const formatSignedAt = signedAtIso => {
  const date = signedAtIso ? new Date(signedAtIso) : new Date()
  if (Number.isNaN(date.getTime())) return signedAtIso || ''
  return date.toISOString().replace('T', ' ').slice(0, 19)
}

/**
 * Stamp a signature block into a PDF.
 *
 * When `role` is provided, content is snapped into that role's fixed stamp zone
 * (to the right of / above "Signature and Date:") so it does not cover the label.
 * Otherwise xPct/yPct (top-origin) are used as a free-form fallback.
 *
 * `decision` ticks the role's Approved / Rejected box, for roles that need one.
 *
 * Layout (bottom → top above the Signature and Date baseline):
 *   Digitally signed on …
 *   Title
 *   Name
 *   [drawn mark or italic name]
 *
 * @param {ArrayBuffer|Uint8Array} pdfBytes
 * @param {Object} options
 * @returns {Promise<Uint8Array>}
 */
export async function stampDcfPdf(pdfBytes, options) {
  const { pngDataUrl, name, title, signedAtIso, page, xPct, yPct, role, decision } = options
  const pdfDoc = await PDFDocument.load(pdfBytes)
  const roleNeedsDecision = Boolean(getDcfSignatureRoleConfig(role)?.requiresDecision)
  const resolvedDecision = roleNeedsDecision ? decision || 'approved' : decision
  applyDecisionCheckbox(pdfDoc, role, resolvedDecision)
  const pages = pdfDoc.getPages()
  const decisionBox = resolvedDecision ? DCF_DECISION_CHECKBOX_RECTS[resolvedDecision] : null
  if (roleNeedsDecision && decisionBox && pages[decisionBox.page - 1]) {
    drawDecisionMark(pages[decisionBox.page - 1], resolvedDecision)
  }
  const placement = role ? getDcfSignaturePlacement(role) : null
  const pageIndex = placement
    ? clamp(placement.page - 1, 0, pages.length - 1)
    : clamp((page || 1) - 1, 0, pages.length - 1)
  const pdfPage = pages[pageIndex]
  const { width: pageWidth, height: pageHeight } = pdfPage.getSize()

  const [regular, italic, bold] = await Promise.all([
    pdfDoc.embedFont(StandardFonts.Helvetica),
    pdfDoc.embedFont(StandardFonts.HelveticaOblique),
    pdfDoc.embedFont(StandardFonts.HelveticaBold),
  ])

  const signerName = (name || '').trim() || 'Unknown'
  const signerTitle = (title || '').trim()
  const metaText = `Digitally signed on ${formatSignedAt(signedAtIso)}`

  const stampBox = placement?.stampPdf
  const markWidth = Math.min(
    MARK_WIDTH,
    stampBox ? stampBox.width : pageWidth * 0.35,
  )
  const pngBytes = toPngBytes(pngDataUrl)
  let markImage = null
  let markHeight = MARK_MIN_HEIGHT

  if (pngBytes) {
    try {
      markImage = await pdfDoc.embedPng(pngBytes)
      markHeight = clamp(
        (markImage.height / markImage.width) * markWidth,
        MARK_MIN_HEIGHT,
        MARK_MAX_HEIGHT,
      )
    } catch {
      markImage = null
    }
  }

  const typedMarkSize = 12
  if (!markImage) markHeight = typedMarkSize + 2

  // Bottom-up stack so the whole block sits above the Signature and Date line.
  const stackLines = [
    { text: metaText, size: META_SIZE, font: regular },
    ...(signerTitle ? [{ text: signerTitle, size: TITLE_SIZE, font: regular }] : []),
    { text: signerName, size: NAME_SIZE, font: bold },
  ]
  const textHeight = stackLines.reduce(
    (total, line, index) => total + line.size + (index ? LINE_GAP : 0),
    0,
  )
  const blockWidth = stackLines.reduce(
    (widest, line) => Math.max(widest, line.font.widthOfTextAtSize(line.text, line.size)),
    markImage ? markWidth : italic.widthOfTextAtSize(signerName, typedMarkSize),
  )
  const blockHeight = markHeight + LINE_GAP + textHeight

  let left
  let blockBottom

  if (stampBox) {
    // Snap beside "Signature and Date:" — left of stamp zone, bottom on the line.
    left = clamp(stampBox.x, EDGE_PADDING, pageWidth - blockWidth - EDGE_PADDING)
    blockBottom = clamp(stampBox.y, EDGE_PADDING, pageHeight - blockHeight - EDGE_PADDING)
  } else {
    const clickX = (clamp(xPct || 0, 0, 100) / 100) * pageWidth
    const clickYFromTop = (clamp(yPct || 0, 0, 100) / 100) * pageHeight
    // Free-form: keep the entire block above the click (Signature and Date line).
    left = clamp(clickX, EDGE_PADDING, Math.max(EDGE_PADDING, pageWidth - blockWidth - EDGE_PADDING))
    const baseline = pageHeight - clickYFromTop
    blockBottom = clamp(baseline, EDGE_PADDING, pageHeight - blockHeight - EDGE_PADDING)
  }

  // Mark sits at the top of the block (furthest above the label).
  const markBottom = blockBottom + textHeight + LINE_GAP

  if (markImage) {
    pdfPage.drawImage(markImage, {
      x: left,
      y: markBottom,
      width: markWidth,
      height: markHeight,
    })
  } else {
    pdfPage.drawText(signerName, {
      x: left,
      y: markBottom + 2,
      size: typedMarkSize,
      font: italic,
      color: rgb(0.08, 0.13, 0.35),
    })
  }

  // Name / title / date between the mark and the Signature and Date baseline.
  let cursor = markBottom - LINE_GAP
  // Draw from top of text stack downward: name, title, meta.
  ;[...stackLines].reverse().forEach(line => {
    cursor -= line.size
    pdfPage.drawText(line.text, {
      x: left,
      y: cursor,
      size: line.size,
      font: line.font,
      color: rgb(0.15, 0.15, 0.15),
    })
    cursor -= LINE_GAP
  })

  return pdfDoc.save()
}

export default stampDcfPdf
